<template>
  <b-container class="my-4 py-4 border-bottom">
    <b-row>
      <b-col>
        <span style="font-size: 1.25em" v-if="annotation.obligationtype"><b-badge :variant="badgeVariant">{{annotation.obligationtype.name}}</b-badge></span> citation: {{annotation.citation}} | id: {{annotation.id}}
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col>
        <div v-if="annotation.topic"><strong>{{annotation.topic}}</strong></div>
        <div>
          <span v-if="savingImport">
            <i class="fa fa-spinner fa-pulse fa-lg fa-fw"></i>
          </span>
          <b-button v-if="allowImport && !savingImport && !imported" class="mr-2" variant="outline-primary" size="sm" @click="importObligation">import</b-button>
          <annotation-history-modal :annotation="annotation"></annotation-history-modal>
          <strong style="font-size: 1.25em"><b-link target="_blank" class="mr-4" :to="{ name: 'RegDecompObligation', params: { id: annotation.id }}">{{annotation.name}}</b-link></strong>
        </div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.active.length > 0">
      <b-col>
        <div v-for="row in annotation.active" :key="'active'+row.id">
          This <strong>{{row.relationship.name}}</strong> {{row.obligation.name}} in <b-link :to="{ name: 'RegDecompWorkspace', params: { id: row.obligation.regulation.id }}" class="mr-2">{{row.obligation.regulation.name_short}}</b-link>
        </div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.passive.length > 0">
      <b-col>
        <div v-for="row in annotation.passive" :key="'passive'+row.id">
          This <strong>{{row.relationship.passive}}</strong> {{row.obligation.name}} in <b-link :to="{ name: 'RegDecompWorkspace', params: { id: row.obligation.regulation.id }}" class="mr-2">{{row.obligation.regulation.name_short}}</b-link>
        </div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="showText">
      <b-col>
        <div><strong>regulatory text</strong></div>
        <div><new-lines :content="annotation.text"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col>
        <div><strong>description</strong></div>
        <div><new-lines :content="annotation.description"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.context">
      <b-col>
        <div><strong>context</strong></div>
        <div><new-lines :content="annotation.context"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.standard">
      <b-col>
        <div><strong>minimum standard</strong></div>
        <div><new-lines :content="annotation.standard"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.lagging">
      <b-col>
        <div><strong>lagging market practice</strong></div>
        <div><new-lines :content="annotation.lagging"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.market_practice">
      <b-col>
        <div><strong>leading market practice</strong></div>
        <div><new-lines :content="annotation.market_practice"></new-lines></div>
      </b-col>
    </b-row>
    <b-row class="mb-2" v-if="annotation.date_effective">
      <b-col>
        <div><strong>effective date</strong></div>
        <div>{{moment(annotation.date_effective).format('YYYY-MM-DD')}}</div>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col>
        <div>{{annotation.updated_by}} - {{moment(annotation.updated_at).format('YYYY-MM-DD HH:mm')}}</div>
      </b-col>
      <b-col>
        <div><strong>status:</strong> {{step.name}}</div>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col>
        <div>
          <b-button v-if="editAlwaysOn" variant="outline-primary" size="sm" @click="$emit('edit', annotation.id)">edit</b-button>
        </div>
      </b-col>
      <b-col>
        <div v-if="editAlwaysOn">
          <span v-if="!savingStep">
            <b-button class="mr-4" v-if="step.downStep && permissionWorkflowDown" variant="outline-primary" size="sm" @click="stepUpdate(step.down)">move to: {{step.downStep.name}}</b-button>
            <b-button v-if="step.upStep && permissionWorkflowUp" variant="outline-primary" size="sm" @click="stepUpdate(step.up)">move to: {{step.upStep.name}}</b-button>
          </span>
          <span v-if="savingStep">
            <i class="fa fa-spinner fa-pulse fa-lg fa-fw"></i>
          </span>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="showEntities" class="mb-4">
      <b-col>
        <div>
          <select-list-props
            :editable="false"
            :editAlwaysOn="editAlwaysOn"
            :labelLinkingField="'entity_id'"
            :linkingModel="'entityobligation'"
            :name="'Applicable entities'"
            :parentId="annotation.id"
            :parentLinkingField="'obligation_id'"
            :valuesProp="annotation.entities"
            :verticesProp="entities"
          ></select-list-props>
        </div>
      </b-col>
      <b-col>
        <div>
          <select-list-props
            :editable="false"
            :editAlwaysOn="editAlwaysOn"
            :labelLinkingField="'area_id'"
            :linkingModel="'areaobligation'"
            :name="'Applicable functions'"
            :parentId="annotation.id"
            :parentLinkingField="'obligation_id'"
            :valuesProp="annotation.areas"
            :verticesProp="areas"
          ></select-list-props>
        </div>
      </b-col>
    </b-row>
    <b-row class="mb-4" v-if="showRequirements">
      <b-col>
        <div>
          <select-list-props
            :editable="false"
            :editAlwaysOn="editAlwaysOn"
            :labelLinkingField="'requirement_id'"
            :linkingModel="'obligationrequirement'"
            :name="'Applicable requirements'"
            :parentId="annotation.id"
            :parentLinkingField="'obligation_id'"
            :valuesProp="annotation.requirements"
            :verticesProp="requirements"
          ></select-list-props>
        </div>
      </b-col>
      <b-col>
        <div>
          <select-list-props
            :editable="false"
            :editAlwaysOn="editAlwaysOn"
            :labelLinkingField="'riskcomponent_id'"
            :linkingModel="'obligationriskcomponent'"
            :name="'Risk framework components'"
            :parentId="annotation.id"
            :parentLinkingField="'obligation_id'"
            :valuesProp="annotation.riskcomponents"
            :verticesProp="riskcomponents"
          ></select-list-props>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="editAlwaysOn">
      <b-col>
        <div v-if="!savingComment">
          <b-form-textarea
            v-model="commentNew"
            placeholder="add comment"
            :rows="2"
            class="mb-2">
          </b-form-textarea>
          <b-button v-if="commentNew" class="mr-2" variant="primary" size="sm" v-on:click="commentAdd">send</b-button>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="comments.length > 0">
      <b-col>
        <div v-for="comment in comments" :key="'comment'+comment.id">
          <div>
            <small>{{comment.username}}: at {{moment(comment.created_at).format('HH:mm')}} on {{moment(comment.created_at).format('YYYY-MM-DD')}}</small>
            <b-button v-if="editAlwaysOn" class="mx-2" variant="outline-primary" size="sm" v-on:click="commentDelete(comment.id)">delete</b-button>
          </div>
          <div class="mb-2">
            {{comment.content}}
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash'
import ac from '@/libs/accesscontrol'
import moment from 'moment'
import workflow from '@/workflows/annotation'
import AnnotationHistoryModal from '@/components/AnnotationHistoryModal.vue'
import SelectListProps from '@/components/sql/SelectListProps.vue'
import NewLines from '@/components/NewLines.vue'

export default {
  name: 'AnnotationItem',
  props: ['allowImport', 'annotation', 'areas', 'editAlwaysOn', 'entities', 'regulation', 'regulationGraphNode', 'requirements', 'riskcomponents', 'showText'],
  components: {
    AnnotationHistoryModal,
    NewLines,
    SelectListProps
  },
  computed: {
    badgeVariant: function () {
      let res = 'primary'
      if (this.annotation.obligationtype) {
        if (this.annotation.obligationtype.name.toLowerCase() === 'obligation') {
          res = 'danger'
        }
        if (this.annotation.obligationtype.name.toLowerCase() === 'entity') {
          res = 'success'
        }
        if (this.annotation.obligationtype.name.toLowerCase() === 'context') {
          res = 'info'
        }
      }
      return res
    },
    permissionWorkflowDown: function () {
      let result = false
      if (this.step.id > 7 && ac.can(this.user.acgroups).readAny('obligationWorkflowApprove').granted) {
        result = true
      }
      if (this.step.id <= 7 && ac.can(this.user.acgroups).updateAny('obligation').granted) {
        result = true
      }
      return result
    },
    permissionWorkflowUp: function () {
      let result = false
      if (this.step.id >= 7 && ac.can(this.user.acgroups).readAny('obligationWorkflowApprove').granted) {
        result = true
      }
      if (this.step.id < 7 && ac.can(this.user.acgroups).updateAny('obligation').granted) {
        result = true
      }
      return result
    },
    showEntities: function () {
      let show = false
      if (this.annotation.obligationtype) {
        if (this.annotation.obligationtype.name.toLowerCase() === 'obligation') {
          show = true
        }
      }
      return show
    },
    showRequirements: function () {
      let show = false
      if (this.annotation.obligationtype) {
        if (this.annotation.obligationtype.name.toLowerCase() === 'obligation') {
          show = true
        }
      }
      return show
    },
    step: function () {
      const step = workflow.find(x => x.id === this.annotation.status_id)
      step.downStep = workflow.find(x => x.id === step.down)
      step.upStep = workflow.find(x => x.id === step.up)
      return step
    },
    user: {
      get () {
        return this.$store.state.user
      }
    }
  },
  created: function () {
    if (this.annotation.comments) {
      this.comments = this.annotation.comments
    }
  },
  data () {
    return {
      commentNew: null,
      comments: [],
      moment: moment,
      history: [],
      historyModal: false,
      imported: false,
      savingComment: false,
      savingImport: false,
      savingStep: false,
      workflow: workflow
    }
  },
  methods: {
    commentAdd: async function () {
      this.savingComment = true
      try {
        // let response = await this.$http(config)
        const params = {
          body: {
            obligation_id: this.annotation.id,
            regulation_id: this.regulation.id,
            content: this.commentNew,
            username: this.user.username
          }
        }
        const result = await this.$Amplify.API.put('cosmos', '/standard/obligationscomment', params)
        params.body.id = result.id
        this.comments.unshift(params.body)
        const paramsLog = {
          body: {
            obligation_id: this.annotation.id,
            regulation_id: this.regulation.id,
            action: 'added comment:' + this.commentNew,
            username: this.user.username
          }
        }
        this.commentNew = null
        this.savingComment = false
        await this.$Amplify.API.put('cosmos', '/standard/obligationslog', paramsLog)
      } catch (e) {
        this.$logger.warn('event saving error: ' + e)
      }
    },
    commentDelete: async function (id) {
      this.savingComment = true
      try {
        await this.$Amplify.API.del('cosmos', `/standard/obligationscomment/${id}`)
        const paramsLog = {
          body: {
            obligation_id: this.annotation.id,
            regulation_id: this.regulation.id,
            action: 'delete comment',
            username: this.user.username
          }
        }
        const temp = this.comments
        _.remove(temp, x => {
          return x.id === id
        })
        this.comments = temp
        this.savingComment = false
        await this.$Amplify.API.put('cosmos', '/standard/obligationslog', paramsLog)
      } catch (e) {
        this.$logger.warn('event saving error: ' + e)
      }
    },
    importObligation: async function () {
      try {
        this.savingImport = true
        const params = {
          body: {
            annotation: this.annotation,
            regulationId: this.regulation.id,
            regulationGraphNode: this.regulationGraphNode,
            user: this.user
          }
        }
        await this.$Amplify.API.put('cosmos', `/obligation/import`, params)
        this.savingImport = false
        this.imported = true
      } catch (e) {
        this.$logger.warn('event saving error: ' + e)
      }
    },
    showHistory: async function () {
      try {
        this.history = await this.$Amplify.API.get('cosmos', `/obligation/${this.annotation.id}/history`)
        this.historyModal = true
      } catch (e) {
        this.$logger.warn('event saving error: ' + e)
      }
    },
    stepUpdate: async function (id) {
      this.savingStep = true
      try {
        // let response = await this.$http(config)
        const params = {
          body: {
            status_id: id
          }
        }
        await this.$Amplify.API.put('cosmos', `/standard/obligation/${this.annotation.id}`, params)
        const paramsLog = {
          body: {
            obligation_id: this.annotation.id,
            regulation_id: this.regulation.id,
            action: 'moved workflow step to ' + this.step.name,
            username: this.user.username
          }
        }
        await this.$Amplify.API.put('cosmos', '/standard/obligationslog', paramsLog)
        this.annotation.status_id = id
      } catch (e) {
        this.$logger.warn('event saving error: ' + e)
      }
      this.savingStep = false
    }
  }
}
</script>

<style scoped>
</style>
